import React, { useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, useNavigate, useSearchParams } from 'react-router-dom';
import useLocalStorage from '../../shared/hooks/useLocalStorage';
import { autoMergeContent } from '../../shared/engine/contentMerger';
import { getQuestionBank, CONTENT_VERSION } from './data/questionBank';
import DrillHome from './components/DrillHome';
import CommandBuilder from './components/CommandBuilder';
import SpeedRound from './components/SpeedRound';
import SyntaxFix from './components/SyntaxFix';

/**
 * Command Syntax Drill root.
 * Manages question bank and drill stats.
 */
export default function CommandDrillApp() {
  const [questions, setQuestions] = useLocalStorage('cmddrill', 'questions', []);
  const [sessions, setSessions] = useLocalStorage('cmddrill', 'sessions', []);

  // Auto-merge: seeds on first load AND adds new questions from code updates
  useEffect(() => {
    autoMergeContent({
      namespace: 'cmddrill',
      key: 'questions',
      seedData: getQuestionBank(),
      seedVersion: CONTENT_VERSION,
      currentData: questions,
      setData: setQuestions,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdateStats = useCallback((questionId, isCorrect, timeTaken) => {
    setQuestions(prev => prev.map(q => {
      if (q.id !== questionId) return q;
      const newAttempts = q.stats.times_attempted + 1;
      const newCorrect = q.stats.times_correct + (isCorrect ? 1 : 0);
      const prevTotal = q.stats.avg_time_seconds * q.stats.times_attempted;
      return {
        ...q,
        stats: {
          times_attempted: newAttempts,
          times_correct: newCorrect,
          avg_time_seconds: Math.round((prevTotal + timeTaken) / newAttempts),
          best_time_seconds: isCorrect
            ? q.stats.best_time_seconds === null
              ? Math.round(timeTaken)
              : Math.min(q.stats.best_time_seconds, Math.round(timeTaken))
            : q.stats.best_time_seconds,
        },
      };
    }));
  }, [setQuestions]);

  return (
    <Routes>
      <Route path="/" element={<DrillHome questions={questions} />} />
      <Route path="/session/:mode" element={
        <DrillSessionRoute
          questions={questions}
          onUpdateStats={handleUpdateStats}
          sessions={sessions}
          setSessions={setSessions}
        />
      } />
    </Routes>
  );
}

function DrillSessionRoute({ questions, onUpdateStats, sessions, setSessions }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = window.location.pathname.split('/').pop();
  const category = searchParams.get('category');

  // Filter and shuffle questions for session
  const sessionQuestions = useMemo(() => {
    let pool = [...questions];

    if (category) {
      pool = pool.filter(q => q.category === category);
    }

    if (mode === 'speed') {
      pool = pool.filter(q => q.mode === 'speed_round' || q.accepted_answers?.[0]?.commands?.length === 1);
    } else if (mode === 'syntaxfix') {
      pool = pool.filter(q => q.mode === 'syntax_fix');
    }

    // Shuffle
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }

    return pool.slice(0, 20); // Max 20 per session
  }, [questions, category, mode]);

  const handleComplete = useCallback((results) => {
    const correct = results.filter(r => r.percentage === 100 || r.isCorrect).length;
    const session = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      mode,
      category: category || 'All',
      questions_count: results.length,
      correct,
      accuracy: results.length > 0 ? correct / results.length : 0,
    };
    setSessions(prev => [...prev, session]);
    navigate('/drill');
  }, [mode, category, setSessions, navigate]);

  if (sessionQuestions.length === 0) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <p className="text-gray-500 mb-4">No questions available for this mode/category.</p>
        <button onClick={() => navigate('/drill')} className="text-green-500 hover:text-green-400 font-mono">
          ← Back to Drill Home
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 py-6">
      {mode === 'speed' ? (
        <SpeedRound
          questions={sessionQuestions}
          onComplete={handleComplete}
          onUpdateStats={onUpdateStats}
        />
      ) : mode === 'syntaxfix' ? (
        <SyntaxFix
          questions={sessionQuestions}
          onComplete={handleComplete}
          onUpdateStats={onUpdateStats}
        />
      ) : (
        <CommandBuilder
          questions={sessionQuestions}
          onComplete={handleComplete}
          onUpdateStats={onUpdateStats}
        />
      )}
    </div>
  );
}
